import { useState } from "react";
import dayjs from "dayjs";
import { Promise as bluebird } from "bluebird";

import { Grid, Typography } from "@mui/material";

import {
  faCalendarCheck,
  faLocationCrosshairs
} from "@fortawesome/free-solid-svg-icons";

import { ToggleButtons } from "@aclymatepackages/atoms";
import { ucFirstLetters } from "@aclymatepackages/formatters";
import { DEFAULT_TONS_CO2E_PER_MILE } from "@aclymatepackages/constants";
import {
  fuelCarbonFromGallons,
  tonsCo2eFromEquipmentFuelGallons
} from "@aclymatepackages/calcs/travel";

import useCsvUploader from "./csvUploader";

import {
  doesFieldExist,
  createErrorObj
} from "../../helpers/components/inputs";
import usePlatformLayoutContext from "../../helpers/contexts/platformLayout";
import { fetchOurApi, fetchPlaceId } from "../../helpers/utils/apiCalls";
import { analyticsTrack } from "../../helpers/analytics";
import { useAccountData, useCachedFirebaseCrud } from "../../helpers/firebase";
import { useSharedFormLoading } from "../../helpers/components/inputs";
import { convertVolumeToGallons } from "../../helpers/components/inputs";

const fieldOptions = [
  { label: "Date", value: "date" },
  { label: "Description", value: "description" },
  { label: "Start Address", value: "from" },
  { label: "End Address", value: "to" },
  { label: "Distance (mi.)", value: "mileage" },
  { label: "Start/Stop Mileage", value: "startStop" },
  { label: "Vehicle", value: "vehicle" },
  { label: "Volume", value: "volume" },
  { label: "Unit", value: "unit" },
  { label: "Fuel Type", value: "fuelType" }
];

const requiredFields = [
  {
    label: "Date",
    description: "When did you take this drive or purchase fuel?",
    icon: faCalendarCheck
  },
  {
    label: "Distance or Fuel",
    description:
      "Either To & From OR Distance, OR Start/Stop Mileage, OR Fuel Volume & Fuel Type",
    icon: faLocationCrosshairs
  }
];

const knownFields = [
  { header: "date", field: "date" },
  { header: "trip purpose", field: "description" },
  { header: "description", field: "description" },
  { header: "start", field: "from" },
  { header: "start address", field: "from" },
  { header: "from", field: "from" },
  { header: "to", field: "to" },
  { header: "end", field: "to" },
  { header: "end address", field: "to" },
  { header: "mileage", field: "mileage" },
  { header: "distance", field: "mileage" },
  { header: "start/stop mileage", field: "startStop" },
  { header: "start/stop", field: "startStop" },
  { header: "vehicle", field: "vehicle" },
  { header: "volume", field: "volume" },
  { header: "gallons", field: "volume" },
  { header: "liters", field: "volume" },
  { header: "litres", field: "volume" },
  { header: "fuel volume", field: "volume" },
  { header: "amount", field: "volume" },
  { header: "quantity", field: "volume" },
  { header: "unit", field: "unit" },
  { header: "units", field: "unit" },
  { header: "volume unit", field: "unit" },
  { header: "unit of measure", field: "unit" },
  { header: "fuel type", field: "fuelType" },
  { header: "fueltype", field: "fuelType" },
  { header: "fuel", field: "fuelType" },
  { header: "type", field: "fuelType" },
  { header: "gas type", field: "fuelType" },
  { header: "gasoline", field: "volume" },
  { header: "diesel", field: "volume" },
  { header: "propane", field: "volume" },
  { header: "lpg", field: "volume" }
];

// Mapping functions for enum values
const isBritishGallonVariant = (value) => {
  return (
    value.includes("british") ||
    value.includes("uk") ||
    value.includes("imperial")
  );
};

const mapGallonType = (value) => {
  if (isBritishGallonVariant(value)) {
    return "british gallons";
  }
  return "gallons";
};

const isLiterVariant = (value) => {
  return value.includes("liter") || value.includes("litre");
};

const mapToValidUnit = (value, allData = []) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const lowerValue = value.toLowerCase().trim();

  const unitMappings = {
    gallon: "gallons",
    gallons: "gallons",
    gal: "gallons",
    "us gallons": "gallons",
    usgallons: "gallons",
    liter: "liters",
    liters: "liters",
    litres: "liters",
    l: "liters",
    "british gallon": "british gallons",
    "british gallons": "british gallons",
    "uk gallons": "british gallons",
    "imperial gallons": "british gallons",
    "imp gal": "british gallons"
  };

  if (unitMappings[lowerValue]) {
    return unitMappings[lowerValue];
  }

  if (lowerValue.includes("gallon")) {
    return mapGallonType(lowerValue);
  }

  if (isLiterVariant(lowerValue)) {
    return "liters";
  }

  const unitCounts = allData.reduce((acc, row) => {
    if (row.unit) {
      const mappedUnit = mapToValidUnit(row.unit);
      if (mappedUnit) {
        acc[mappedUnit] = (acc[mappedUnit] || 0) + 1;
      }
    }
    return acc;
  }, {});

  if (Object.keys(unitCounts).length > 0) {
    return Object.keys(unitCounts).reduce((a, b) =>
      unitCounts[a] > unitCounts[b] ? a : b
    );
  }

  return "gallons";
};

const isGasolineVariant = (value) => {
  return value.includes("gas") && !value.includes("diesel");
};

const isDieselVariant = (value) => {
  return value.includes("diesel");
};

const isPetrolVariant = (value) => {
  return value.includes("petrol") || value.includes("unleaded");
};

const mapToValidFuelType = (value, allData = []) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const lowerValue = value.toLowerCase().trim();

  const fuelTypeMappings = {
    gas: "gas",
    gasoline: "gas",
    petrol: "gas",
    unleaded: "gas",
    regular: "gas",
    premium: "gas",
    diesel: "diesel",
    "diesel fuel": "diesel",
    gasoil: "diesel",
    "gas oil": "diesel",
    propane: "lpg"
  };

  if (fuelTypeMappings[lowerValue]) {
    return fuelTypeMappings[lowerValue];
  }

  if (isGasolineVariant(lowerValue)) {
    return "gas";
  }

  if (isDieselVariant(lowerValue)) {
    return "diesel";
  }

  if (isPetrolVariant(lowerValue)) {
    return "gas";
  }

  const fuelTypeCounts = allData
    .map((row) => row.fuelType)
    .filter(Boolean)
    .map((fuelType) => mapToValidFuelType(fuelType))
    .filter(Boolean)
    .reduce(
      (acc, fuelType) => ({
        ...acc,
        [fuelType]: (acc[fuelType] || 0) + 1
      }),
      {}
    );

  if (Object.keys(fuelTypeCounts).length > 0) {
    return Object.keys(fuelTypeCounts).reduce((a, b) =>
      fuelTypeCounts[a] > fuelTypeCounts[b] ? a : b
    );
  }

  return "gas";
};

const fieldValidator = (headers) => {
  if (!doesFieldExist(headers, "date")) {
    return createErrorObj('Your CSV must include a "date" field');
  }

  const hasMileageData =
    (doesFieldExist(headers, "from") && doesFieldExist(headers, "to")) ||
    doesFieldExist(headers, "mileage") ||
    doesFieldExist(headers, "startStop");

  const hasFuelData =
    doesFieldExist(headers, "volume") && doesFieldExist(headers, "fuelType");

  if (!hasMileageData && !hasFuelData) {
    return createErrorObj(
      'Your CSV must include either mileage data ("To" and "From" fields, a "Distance" field or a "Start/Stop Mileage" field) OR fuel data ("Volume" and "Fuel Type" fields)'
    );
  }

  return { success: true };
};

const calculateFuelEmissions = (trip, vehicleType) => {
  const { volume = "", unit, fuelType } = trip;
  if (!volume || !fuelType) {
    return trip;
  }

  const gallons = convertVolumeToGallons(
    typeof volume === "string" ? Number(volume.split(",").join()) : volume,
    unit || "gallons"
  );
  const tonsCo2e =
    vehicleType === "commercial"
      ? tonsCo2eFromEquipmentFuelGallons({
          gallons,
          fuelType,
          equipmentType: "industrial"
        })
      : fuelCarbonFromGallons(gallons, fuelType);

  return {
    ...trip,
    gallons,
    tonsCo2e,
    calculationMethod: "fuel"
  };
};

const calculateMileageEmissions = (trip, tonsCo2ePerMile) => {
  const { mileage } = trip;
  return {
    ...trip,
    tonsCo2e: Number(mileage) * tonsCo2ePerMile,
    calculationMethod: "mileage"
  };
};

const calculateStartStopEmissions = (trip, tonsCo2ePerMile) => {
  const { startStop } = trip;
  const [start, stop] = startStop.replace(" ", "").split(/[^0-9.]/);
  const estimatedMileage = Math.abs(Number(stop) - Number(start));

  return {
    ...trip,
    mileage: estimatedMileage,
    tonsCo2e: estimatedMileage * tonsCo2ePerMile,
    calculationMethod: "startStop"
  };
};

const calculateDirectionsEmissions = async (trip, tonsCo2ePerMile) => {
  const { to, from } = trip;
  const { totalMileage } = await fetchOurApi({
    path: "/calcs/cars/directions-mileage",
    method: "POST",
    data: { to, from },
    callback: (res) => res
  });

  return {
    ...trip,
    mileage: totalMileage,
    tonsCo2e: totalMileage * tonsCo2ePerMile,
    calculationMethod: "directions"
  };
};

const findSubcategory = ({ vehicleType, vehicleOwnership }) => {
  if (vehicleType === "passenger") {
    const subcategory =
      vehicleOwnership === "company" ? "company-vehicles" : "rental-cars";

    return subcategory;
  }

  const subcategory =
    vehicleOwnership === "company" ? "equipment-fuel" : "rented-equipment";

  return subcategory;
};

const csvTripsCarbonData = async (trips, vehicleType) => {
  return await Promise.all(
    trips.map(async (trip) => {
      console.log("Processing trip: ", trip);
      const { mileage, startStop, volume, fuelType } = trip;

      if (volume && fuelType) {
        return calculateFuelEmissions(trip, vehicleType);
      }

      if (mileage) {
        return calculateMileageEmissions(trip, DEFAULT_TONS_CO2E_PER_MILE);
      }

      if (startStop) {
        return calculateStartStopEmissions(trip, DEFAULT_TONS_CO2E_PER_MILE);
      }

      return await calculateDirectionsEmissions(
        trip,
        DEFAULT_TONS_CO2E_PER_MILE
      );
    })
  );
};

const ToggleInput = ({ value, setValue, buttons, label }) => (
  <Grid container spacing={1} wrap="nowrap" alignItems="center">
    <Grid item>
      <Typography variant="body1">{label}</Typography>
    </Grid>
    <Grid item>
      <ToggleButtons
        value={value}
        onChange={setValue}
        buttons={buttons}
        style={{ margin: "auto" }}
      />
    </Grid>
  </Grid>
);

const useProcessFuelMileageTrips = ({ setOpen, fileInfo }) => {
  const { setFormLoading } = useSharedFormLoading();
  const { activateSnackbar } = usePlatformLayoutContext();
  const { newCollectionDoc } = useCachedFirebaseCrud();

  const fetchToAndFromPlaceIds = async ({ to, from }) => {
    const [toPlaceId, fromPlaceId] = await Promise.all([
      fetchPlaceId(to),
      fetchPlaceId(from)
    ]);

    return { toPlaceId, fromPlaceId };
  };

  const createToAndFromObj = async (to, from) => {
    if (!to || !from) {
      return {};
    }

    const { toPlaceId, fromPlaceId } = await fetchToAndFromPlaceIds({
      to,
      from
    });

    return {
      to: {
        description: to,
        placeId: toPlaceId
      },
      from: {
        description: from,
        placeId: fromPlaceId
      }
    };
  };

  const preProcessTrips = async (completeTrips, subcategory) =>
    await Promise.all(
      completeTrips.map(async (trip) => {
        const { to, from } = trip;
        const toAndFromObj = await createToAndFromObj(to, from);

        return {
          ...trip,
          ...toAndFromObj,
          roundTrip: false,
          source: "csv-upload",
          ...(fileInfo?.name ? { csvName: fileInfo.name } : {}),
          status: "confirmed",
          subcategory,
          vendor: `${dayjs().format("MM/DD/YYYY")} ${ucFirstLetters(
            subcategory.split("-").join(" ")
          )} Emissions`,
          archived: false
        };
      })
    );

  const batchTrips = (trips) => {
    const BATCH_SIZE = 500;

    return Array.from(
      {
        length: Math.ceil(trips.length / BATCH_SIZE)
      },
      (_, batchIndex) =>
        trips.slice(
          batchIndex * BATCH_SIZE,
          batchIndex * BATCH_SIZE + BATCH_SIZE
        )
    );
  };

  const saveBatchOfTrips = (batchOfTrips) =>
    Promise.all(
      batchOfTrips.map((trip) => newCollectionDoc("transactions", trip))
    );

  return async (csv, inputData) => {
    setFormLoading(true);

    const formattedTrips = await preProcessTrips(
      csv,
      findSubcategory(inputData)
    );
    const batchedTrips = batchTrips(formattedTrips);

    analyticsTrack("Fuel and Mileage Data Loaded", {
      numberOfTransactions: formattedTrips.length
    });

    await bluebird.map(batchedTrips, saveBatchOfTrips);

    const snackbarMessage = `${formattedTrips.length} fuel and mileage entries were added to your account.`;
    activateSnackbar({ message: snackbarMessage, alert: "success" });
    setFormLoading(false);
    return setOpen(false);
  };
};

const useFuelCsvUploader = ({ open, setOpen }) => {
  const [{ startDate }] = useAccountData();

  const [fileInfo, setFileInfo] = useState({});
  const [matchedHeaders, setMatchedHeaders] = useState([]);
  const [vehicleOwnership, setVehicleOwnership] = useState("company");
  const [vehicleType, setVehicleType] = useState("passenger");

  const processFuelMileageTrips = useProcessFuelMileageTrips({
    setOpen,
    fileInfo
  });

  const matchedVehicleField = matchedHeaders.some(
    (field) => field.field === "vehicle"
  );

  const normalizeEnumValues = (trip, allData) => {
    return {
      ...trip,
      ...(trip.unit ? { unit: mapToValidUnit(trip.unit, allData) } : {}),
      ...(trip.fuelType
        ? { fuelType: mapToValidFuelType(trip.fuelType, allData) }
        : {})
    };
  };

  const hasValidDataForProcessing = ({
    date,
    startStop,
    mileage,
    to,
    from,
    volume,
    fuelType
  }) => {
    return (
      !!date &&
      (!!startStop || !!mileage || !!to || !!from || (!!volume && !!fuelType))
    );
  };

  const isCompleteTrip =
    (matchedVehicleField) =>
    ({ date, mileage, startStop, from, to, vehicle, volume, fuelType }) => {
      const hasMileageData = !!mileage || !!startStop || (!!from && !!to);
      const hasFuelData = !!volume && !!fuelType;
      const hasVehicleIfRequired = matchedVehicleField ? !!vehicle : true;

      return !!date && (hasMileageData || hasFuelData) && hasVehicleIfRequired;
    };

  const finalizeTrip = (trip) => {
    return {
      ...trip,
      date: dayjs(trip.date).toDate(),
      ...(trip.volume ? { volume: Number(trip.volume) } : {})
    };
  };

  const findCompleteTrips = (dataObjects) => {
    const normalizedData = dataObjects.map((trip) =>
      normalizeEnumValues(trip, dataObjects)
    );

    const fixableTrips = normalizedData.filter(hasValidDataForProcessing);

    const completeTripValidator = isCompleteTrip(matchedVehicleField);

    return fixableTrips.filter(completeTripValidator).map(finalizeTrip);
  };

  const processCsvData = async ({ data: parsedCsvData }) => {
    const completeTrips = findCompleteTrips(parsedCsvData);

    const isDateBeforeStartDate = (date) =>
      dayjs(new Date(date)).isBefore(dayjs(startDate));

    const validTrips = startDate
      ? completeTrips.filter(({ date }) => !isDateBeforeStartDate(date))
      : completeTrips;

    const carbonTripData = await csvTripsCarbonData(validTrips, vehicleType);

    return processFuelMileageTrips(carbonTripData, {
      vehicleOwnership,
      vehicleType
    });
  };

  const mileageCsvUploaderSteps = useCsvUploader({
    knownFields,
    fieldOptions,
    open,
    setOpen,
    fileInfo,
    setFileInfo,
    matchedHeaders,
    setMatchedHeaders,
    processData: processCsvData,
    fieldValidator,
    docType: "fuel-mileage",
    title: "Upload your fuel OR mileage data",
    firstStepText:
      "We'll automatically calculate your carbon emissions for the drives and fuel purchases that you upload",
    requiredFields,
    successMessage: `Your fuel and mileage data were added to your account but it might take a minute for them to appear`
  });

  const completeFuelMileageForm = [
    {
      label: "Who owns this vehicle?",
      input: (
        <ToggleInput
          label="Who owns this vehicle?"
          value={vehicleOwnership}
          setValue={setVehicleOwnership}
          buttons={[
            {
              value: "company",
              name: "The Company"
            },
            {
              value: "rental",
              name: "It's a Rental"
            }
          ]}
        />
      )
    },
    {
      label: "What type of vehicle is this?",
      input: (
        <ToggleInput
          label="What type of vehicle was this fuel purchased for?"
          value={vehicleType}
          setValue={setVehicleType}
          buttons={[
            {
              value: "passenger",
              name: "Passenger"
            },
            {
              value: "commercial",
              name: "Commercial/industrial"
            }
          ]}
        />
      )
    },
    ...mileageCsvUploaderSteps
  ];

  return [completeFuelMileageForm];
};
export default useFuelCsvUploader;
